import * as vp from 'vopay';
import { FunctionComponent, useContext, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { DialogueHandler, AgreementDialogueHandler } from './types';
import { useParams } from 'react-router-dom';
import AgreementContext from './AgreementContext';
import AccountContext from '../AccountContext';

const { ConfirmationDialogue } = vp;

type AgreementDialogueProps = {
  handleDialogue: DialogueHandler<AgreementDialogueHandler>;
};

type FC = FunctionComponent<AgreementDialogueProps>;

const AgreementDialogue: FC = ({ handleDialogue }: AgreementDialogueProps) => {
  const { t } = useTranslation('translations', { keyPrefix: 'AgreementDialogue' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const { showVoidAgreementConfirmAlert, showViewAgreementFailAlert } = useContext(AgreementContext);
  const { details } = useContext(AccountContext);
  const { accountId } = useParams<{ accountId: string }>();
  return useMemo(
    () => (
      <div>
        {/* Void Agreement */}
        <ConfirmationDialogue
          isVisible={showVoidAgreementConfirmAlert}
          title={t('VoidAgreement.title', { defaultValue: 'Void Agreement' })}
          subTitle={t('VoidAgreement.subTitle', {
            defaultValue:
              'All agreement information will be deleted. Are you sure you wish to void {{accountName}}’s agreement?',
            accountName: details[accountId].accountName,
          })}
          status="alert"
          confirm={{
            label: tGlobal('Buttons.back'),
            action: () => handleDialogue({ type: 'void', state: 'back' }),
          }}
          cancel={{
            label: tGlobal('Buttons.confirm'),
            action: () => handleDialogue({ type: 'void', state: 'confirm' }),
          }}
        />

        {
          /* View Agreement */
          <ConfirmationDialogue
            isVisible={showViewAgreementFailAlert}
            title={t('NoAgreement.title', { defaultValue: 'No Agreement Found' })}
            subTitle={t('NoAgreement.subTitle', {
              defaultValue:
                'Cannot view agreement PDF as no agreement has been generated. Please add signors to start agreement generation process.',
            })}
            status="alert"
            confirm={{
              label: tGlobal('Buttons.back'),
              action: () => handleDialogue({ type: 'view', state: 'back' }),
            }}
            cancel={{
              label: t('NoAgreement.addSigners', { defaultValue: 'Add Signers' }),
              action: () => handleDialogue({ type: 'view', state: 'confirm' }),
            }}
          />
        }
      </div>
    ),
    [showVoidAgreementConfirmAlert, showViewAgreementFailAlert, details, accountId, t, tGlobal],
  );
};

export default AgreementDialogue;
